import { registerSharedDependency } from 'core-components/helpers/MFHelper'
import { getTokens } from 'src/core/classes/helpers/StorageHelper'
import { WSClient } from 'src/core/classes/ws/WSClient'
import Logger from 'src/core/utils/Logger'

import { EWSEvent, IWSClient } from './IWSClient'

class WSConnectionPool {
  public readonly wsClientMap: Map<string, IWSClient> = new Map()
  private handledAuthClients = new Set<string>()

  public initWSPool(wsData: string) {
    try {
      const wsURLs: { [key: string]: string } = JSON.parse(wsData || '{}') || {}
      for (const [code, url] of Object.entries(wsURLs)) {
        const client = new WSClient(url)
        this.addClient(code, client)
      }
    } catch (err: unknown) {
      Logger.error('WSConnectionPool::: Wrong init data', err)
    }
  }

  public addClient(code: string, client: IWSClient) {
    this.wsClientMap.set(code, client)
  }

  public async getClient(code: string) {
    const client = this.wsClientMap.get(code) || null
    if (client) {
      if (!client.ws || client.ws.readyState === client.ws.CLOSED) {
        await client.connect()
      }
    }

    if (!client) {
      throw new Error(`[${code}] Websocket client does not exist`)
    }

    return client
  }

  /**
   * Method is waiting when flag isAuthorized will be resolved. Make sure that logic for resolving flag is added
   *
   * @param code
   */
  public async getAuthorizedClient(code: string) {
    const client = this.wsClientMap.get(code) || null

    if (!client) {
      throw new Error(`[${code}] Websocket client does not exist`)
    }

    if (!client.ws || client.ws.readyState === client.ws.CLOSED) {
      await client.connect()
    }

    await client.isAuthorized.isResolved()

    return client
  }

  public addListener(
    code: string,
    event: EWSEvent | string,
    listener: (client: IWSClient) => void
  ) {
    const client = this.wsClientMap.get(code) || null
    let unsubscribe = () => {}

    if (client) {
      unsubscribe = client.on(event, () => {
        listener(client)
      })
    }

    return unsubscribe
  }

  public updateAuthToken = async () => {
    const [token] = getTokens()
    const promises: Promise<unknown>[] = []
    this.wsClientMap.forEach((client) => {
      if (client.isAuthorized.resolved) {
        promises.push(client.call('auth', 'token', { token }, true))
      }
    })
    return Promise.all(promises)
  }

  public addAuthListener(code: string) {
    const wsClient = this.wsClientMap.get(code) || null
    const isHandled = this.handledAuthClients.has(code)

    if (wsClient?.isAuthorized.resolved || isHandled) {
      return
    }

    const handleOpen = async (client: IWSClient) => {
      const [token] = getTokens()
      if (token) {
        const response = await client.call('auth', 'token', { token }, true)
        if (response.status !== 'success') {
          throw new Error('Was not able to authorize')
        }
      }

      client.isAuthorized.resolve()
      client.isAuthorized.resolved = true
    }

    this.handledAuthClients.add(code)

    if (wsClient?.isConnected) {
      handleOpen(wsClient)
      return
    }

    return this.addListener(code, EWSEvent.open, handleOpen)
  }
}

export const wsConnectionPool = registerSharedDependency<WSConnectionPool>(
  'wsConnectionPool',
  () => new WSConnectionPool()
)
